import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../services/api';

interface LocationDisplayProps {
  objet: any;
  onLocationChange?: (zoneId: string, boxId: string) => void;
  isEditable?: boolean;
  className?: string;
}

const LocationDisplay: React.FC<LocationDisplayProps> = ({ 
  objet, 
  onLocationChange, 
  isEditable = false,
  className = ""
}) => {
  const [selectedSite, setSelectedSite] = useState('');
  const [selectedBatiment, setSelectedBatiment] = useState('');
  const [selectedPiece, setSelectedPiece] = useState('');
  const [selectedZone, setSelectedZone] = useState('');
  const [selectedBox, setSelectedBox] = useState('');

  // Charger les sites
  const { data: sites = [] } = useQuery({
    queryKey: ['sites'],
    queryFn: async () => {
      const response = await apiClient.get('/sites');
      return response.data;
    },
  });

  // Charger les bâtiments du site sélectionné
  const { data: batiments = [] } = useQuery({
    queryKey: ['batiments', selectedSite],
    queryFn: async () => {
      if (!selectedSite) return [];
      const response = await apiClient.get(`/batiments?siteId=${selectedSite}`);
      return response.data;
    },
    enabled: !!selectedSite,
  });

  // Charger les pièces du bâtiment sélectionné
  const { data: pieces = [] } = useQuery({
    queryKey: ['pieces', selectedBatiment],
    queryFn: async () => {
      if (!selectedBatiment) return [];
      const response = await apiClient.get(`/pieces?batimentId=${selectedBatiment}`);
      return response.data;
    },
    enabled: !!selectedBatiment,
  });

  // Charger les zones de la pièce sélectionnée
  const { data: zones = [] } = useQuery({
    queryKey: ['zones', selectedPiece],
    queryFn: async () => {
      if (!selectedPiece) return [];
      const response = await apiClient.get(`/zones?pieceId=${selectedPiece}`);
      return response.data;
    },
    enabled: !!selectedPiece,
  });

  // Charger les boxes de la pièce sélectionnée
  const { data: boxes = [] } = useQuery({
    queryKey: ['boxes', selectedPiece],
    queryFn: async () => {
      if (!selectedPiece) return [];
      const response = await apiClient.get(`/boxes?pieceId=${selectedPiece}`);
      return response.data;
    },
    enabled: !!selectedPiece,
  });

  // Initialiser les valeurs depuis l'objet
  useEffect(() => {
    if (objet) {
      // Déterminer la hiérarchie à partir de la zone ou box
      if (objet.zoneId) {
        const zone = zones.find(z => z.id === objet.zoneId);
        if (zone) {
          setSelectedZone(objet.zoneId);
          setSelectedPiece(zone.pieceId);
          // Remonter la hiérarchie
          const piece = pieces.find(p => p.id === zone.pieceId);
          if (piece) {
            setSelectedPiece(piece.id);
            setSelectedBatiment(piece.batimentId);
            // Remonter encore
            const batiment = batiments.find(b => b.id === piece.batimentId);
            if (batiment) {
              setSelectedBatiment(batiment.id);
              setSelectedSite(batiment.siteId);
            }
          }
        }
      }
      if (objet.boxId) {
        const box = boxes.find(b => b.id === objet.boxId);
        if (box) {
          setSelectedBox(objet.boxId);
          setSelectedPiece(box.pieceId);
          // Remonter la hiérarchie
          const piece = pieces.find(p => p.id === box.pieceId);
          if (piece) {
            setSelectedPiece(piece.id);
            setSelectedBatiment(piece.batimentId);
            const batiment = batiments.find(b => b.id === piece.batimentId);
            if (batiment) {
              setSelectedBatiment(batiment.id);
              setSelectedSite(batiment.siteId);
            }
          }
        }
      }
    }
  }, [objet, zones, boxes, pieces, batiments]);

  const handleSiteChange = (siteId: string) => {
    setSelectedSite(siteId);
    setSelectedBatiment('');
    setSelectedPiece('');
    setSelectedZone('');
    setSelectedBox('');
    if (onLocationChange) {
      onLocationChange('', '');
    }
  };

  const handleBatimentChange = (batimentId: string) => {
    setSelectedBatiment(batimentId);
    setSelectedPiece('');
    setSelectedZone('');
    setSelectedBox('');
    if (onLocationChange) {
      onLocationChange('', '');
    }
  };

  const handlePieceChange = (pieceId: string) => {
    setSelectedPiece(pieceId);
    setSelectedZone('');
    setSelectedBox('');
    if (onLocationChange) {
      onLocationChange('', '');
    }
  };

  const handleZoneChange = (zoneId: string) => {
    setSelectedZone(zoneId);
    setSelectedBox('');
    if (onLocationChange) {
      onLocationChange(zoneId, '');
    }
  };

  const handleBoxChange = (boxId: string) => {
    setSelectedBox(boxId);
    if (onLocationChange) {
      onLocationChange(selectedZone, boxId);
    }
  };

  const getCurrentLocation = () => {
    if (objet.boxId) {
      const box = boxes.find(b => b.id === objet.boxId);
      const zone = zones.find(z => z.id === box?.currentZoneId);
      const piece = pieces.find(p => p.id === box?.pieceId);
      const batiment = batiments.find(b => b.id === piece?.batimentId);
      const site = sites.find(s => s.id === batiment?.siteId);
      return `📦 ${box?.nom || objet.boxId} (${zone?.nom || 'Zone inconnue'}) - ${piece?.nom || ''} - ${batiment?.nom || ''} - ${site?.nom || ''}`;
    } else if (objet.zoneId) {
      const zone = zones.find(z => z.id === objet.zoneId);
      const piece = pieces.find(p => p.id === zone?.pieceId);
      const batiment = batiments.find(b => b.id === piece?.batimentId);
      const site = sites.find(s => s.id === batiment?.siteId);
      return `🏠 ${zone?.nom || objet.zoneId} - ${piece?.nom || ''} - ${batiment?.nom || ''} - ${site?.nom || ''}`;
    }
    return '📍 Non localisé';
  };

  if (!isEditable) {
    return (
      <div className={`text-sm text-gray-600 ${className}`}>
        <span className="font-medium">Emplacement:</span> {getCurrentLocation()}
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`}>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Site
        </label>
        <select
          value={selectedSite}
          onChange={(e) => handleSiteChange(e.target.value)}
          className="w-full p-3 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">Sélectionner un site</option>
          {sites.map(site => (
            <option key={site.id} value={site.id}>
              {site.nom}
            </option>
          ))}
        </select>
      </div>

      {selectedSite && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Bâtiment
          </label>
          <select
            value={selectedBatiment}
            onChange={(e) => handleBatimentChange(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Sélectionner un bâtiment</option>
            {batiments.map(batiment => (
              <option key={batiment.id} value={batiment.id}>
                {batiment.nom}
              </option>
            ))}
          </select>
        </div>
      )}

      {selectedBatiment && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Pièce
          </label>
          <select
            value={selectedPiece}
            onChange={(e) => handlePieceChange(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Sélectionner une pièce</option>
            {pieces.map(piece => (
              <option key={piece.id} value={piece.id}>
                {piece.nom}
              </option>
            ))}
          </select>
        </div>
      )}

      {selectedPiece && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Zone/Armoire
          </label>
          <select
            value={selectedZone}
            onChange={(e) => handleZoneChange(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Sélectionner une zone</option>
            {zones.map(zone => (
              <option key={zone.id} value={zone.id}>
                {zone.nom}
              </option>
            ))}
          </select>
        </div>
      )}

      {selectedZone && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Box Mobile (optionnel)
          </label>
          <select
            value={selectedBox}
            onChange={(e) => handleBoxChange(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Aucune box (dans la zone)</option>
            {boxes.map(box => (
              <option key={box.id} value={box.id}>
                {box.nom}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

export default LocationDisplay;

